import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
  Table as TanStackTable,
  Row,
} from "@tanstack/react-table";
import { ArrowUpDown, Phone } from "lucide-react";
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InboxArrowDownIcon,
} from "@heroicons/react/24/outline";
import { CallRecord } from "@/types/callFilters";
import { format, isToday, isYesterday, isThisWeek } from "date-fns";
import { parseApiTimestamp, timestampMs } from "@/lib/datetime";

interface CallsTableProps {
  calls: CallRecord[];
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  selectedCalls?: CallRecord[];
  onCallSelect?: (call: CallRecord) => void;
  onCallClick?: (call: CallRecord) => void;
  isSelectEnabled?: boolean;
}

const CallsTable: React.FC<CallsTableProps> = ({
  calls,
  sorting,
  setSorting,
  selectedCalls = [],
  onCallSelect,
  onCallClick,
  isSelectEnabled = false,
}) => {
  const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "completed":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <CheckCircleIcon className="h-5 w-5 text-success" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Completed</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case "pending":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <ClockIcon className="h-5 w-5 text-warning" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Pending</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case "in_progress":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Phone className="h-5 w-5 text-info animate-pulse" />
              </TooltipTrigger>
              <TooltipContent>
                <p>In Progress</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case "failed":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <XCircleIcon className="h-5 w-5 text-destructive" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Failed</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case "voicemail":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <InboxArrowDownIcon className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Voicemail</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      default:
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <ExclamationTriangleIcon className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{status}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
    }
  };

  const getStrategyTypeBadge = (strategyType: string) => {
    const typeUpper = strategyType?.toUpperCase() || "";
    switch (typeUpper) {
      case "CONSENT":
        return (
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-border rounded-full"></div>
            <span>Consent</span>
          </div>
        );
      case "CHECK_IN":
        return (
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
            <span>Check-in</span>
          </div>
        );
      default:
        // Format any other strategy type to be human-readable
        const formattedType = strategyType
          ? strategyType
              .toLowerCase()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())
          : "Unknown";
        return (
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
            <span>{formattedType}</span>
          </div>
        );
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      // API timestamps without an offset are UTC; shown in local time.
      const date = parseApiTimestamp(dateString);
      if (!date) return dateString;

      if (isToday(date)) {
        // Today: just show time
        return format(date, "h:mm a");
      } else if (isYesterday(date)) {
        // Yesterday: "Yesterday 2:30 PM"
        return `Yesterday ${format(date, "h:mm a")}`;
      } else if (isThisWeek(date)) {
        // This week: "Mon 2:30 PM"
        return format(date, "EEE h:mm a");
      } else {
        // Older: "Jan 15 2:30 PM"
        return format(date, "MMM d h:mm a");
      }
    } catch {
      return dateString;
    }
  };

  const formatDuration = (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) return "—";
    try {
      const durationMs = timestampMs(endTime) - timestampMs(startTime);
      if (Number.isNaN(durationMs)) return "—";
      const durationSec = Math.floor(durationMs / 1000);
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    } catch {
      return "—";
    }
  };

  const columns: ColumnDef<CallRecord>[] = [
    ...(isSelectEnabled
      ? [
          {
            id: "select",
            header: ({ table }: { table: TanStackTable<CallRecord> }) => (
              <Checkbox
                checked={
                  table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) =>
                  table.toggleAllPageRowsSelected(!!value)
                }
                aria-label="Select all calls"
              />
            ),
            cell: ({ row }: { row: Row<CallRecord> }) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select call"
              />
            ),
            enableSorting: false,
            enableHiding: false,
          },
        ]
      : []),
    {
      accessorKey: "patient_name",
      header: "Patient",
      cell: ({ row }) => {
        const firstName = row.original.patient_first_name;
        const lastName = row.original.patient_last_name;
        const phoneNumber = row.original.callee_phone_number;

        // Show patient name if available, otherwise show phone number
        const displayName =
          firstName && lastName ? `${firstName} ${lastName}` : phoneNumber;

        return <div className="text-sm">{displayName}</div>;
      },
    },
    {
      accessorKey: "call_status",
      header: () => <div className="text-center">Status</div>,
      cell: ({ row }) => (
        <div className="flex justify-center">
          {getStatusIcon(row.getValue("call_status"))}
        </div>
      ),
    },
    {
      accessorKey: "strategy_version.strategy_type",
      header: "Type",
      cell: ({ row }) => {
        const strategyType = row.original.strategy_version?.strategy_type;
        return strategyType ? getStrategyTypeBadge(strategyType) : "—";
      },
    },
    {
      accessorKey: "call_started_at",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 font-medium text-sm"
        >
          Started At
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const startedAt = row.getValue("call_started_at") as string;
        return (
          <div className="text-sm">
            {startedAt ? formatDateTime(startedAt) : "—"}
          </div>
        );
      },
    },
    {
      id: "duration",
      header: "Duration",
      cell: ({ row }) => (
        <div className="text-sm">
          {formatDuration(
            row.original.call_started_at,
            row.original.call_ended_at
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: calls,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      rowSelection: selectedCalls.reduce((acc, call) => {
        const index = calls.findIndex((c) => c.id === call.id);
        if (index >= 0) acc[index] = true;
        return acc;
      }, {} as Record<string, boolean>),
    },
    onRowSelectionChange: (updater) => {
      if (!onCallSelect) return;

      const currentSelection = table.getState().rowSelection;
      const newSelection =
        typeof updater === "function" ? updater(currentSelection) : updater;

      if (!newSelection) return;

      // Determine which calls were selected/deselected
      Object.entries(newSelection).forEach(([index, isSelected]) => {
        const call = calls[parseInt(index)];
        if (
          call &&
          isSelected !== selectedCalls.some((c) => c.id === call.id)
        ) {
          onCallSelect(call);
        }
      });
    },
    enableRowSelection: isSelectEnabled,
  });

  const getStatusLabel = (status: string) => {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "completed":
        return "Completed";
      case "pending":
        return "Pending";
      case "in_progress":
        return "In Progress";
      case "failed":
        return "Failed";
      case "voicemail":
        return "Voicemail";
      default:
        return status || "Unknown";
    }
  };

  const getStatusBadgeClass = (status: string) => {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "completed":
        return "bg-success/10 text-success border-success/30";
      case "pending":
        return "bg-warning/10 text-warning border-warning/30";
      case "in_progress":
        return "bg-info/10 text-info border-info/30";
      case "failed":
        return "bg-destructive/10 text-destructive border-destructive/30";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const rows = table.getRowModel().rows;

  return (
    <div className="flex flex-1 min-w-0 flex-col overflow-visible rounded-xl border bg-card">
        {/* Phone: card list (same navigation as the table row click) */}
        <div className="md:hidden divide-y">
          {rows.length ? (
            rows.map((row) => {
              const call = row.original;
              const firstName = call.patient_first_name;
              const lastName = call.patient_last_name;
              const displayName =
                firstName && lastName
                  ? `${firstName} ${lastName}`
                  : call.callee_phone_number;
              const strategyType = call.strategy_version?.strategy_type;
              const isSelected = row.getIsSelected();
              return (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  data-state={isSelected && "selected"}
                  className="flex cursor-pointer items-start gap-3 p-3 hover:bg-row-hover active:bg-row-hover data-[state=selected]:bg-muted"
                  onClick={() => onCallClick?.(call)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onCallClick?.(call);
                    }
                  }}
                >
                  {isSelectEnabled && (
                    <div
                      className="pt-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select call"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {displayName}
                        </div>
                        {firstName && lastName && call.callee_phone_number && (
                          <div className="truncate text-xs text-muted-foreground">
                            {call.callee_phone_number}
                          </div>
                        )}
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${getStatusBadgeClass(
                          call.call_status
                        )}`}
                      >
                        {getStatusLabel(call.call_status)}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {strategyType ? getStrategyTypeBadge(strategyType) : <span>—</span>}
                      <span>
                        {call.call_started_at
                          ? formatDateTime(call.call_started_at)
                          : "—"}
                      </span>
                      <span>
                        {formatDuration(call.call_started_at, call.call_ended_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex h-24 flex-col items-center justify-center gap-2 p-4">
              <Phone className="h-8 w-8 text-muted-foreground" />
              <p className="text-muted-foreground text-sm">No calls found</p>
            </div>
          )}
        </div>

        {/* Tablet / desktop: table */}
        <div className="hidden md:block">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="py-2 px-4 text-sm font-medium"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-row-hover"
                  onClick={() => onCallClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="py-3 px-4"
                      onClick={
                        cell.column.id === "select" ||
                        cell.column.id === "actions"
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center py-2 px-4"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Phone className="h-8 w-8 text-muted-foreground" />
                    <p className="text-muted-foreground text-sm">
                      No calls found
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </div>
    </div>
  );
};

export default CallsTable;
